import React from 'react';
import { MapPin, Phone, Clock, Navigation, Truck } from 'lucide-react';
import { restaurantInfo, openingHours, deliveryZones } from '../data/mock';
import OpenStatusIndicator from '../components/OpenStatusIndicator';

const Contact = () => {
  return (
    <div className="min-h-screen vibrant-gradient-bg py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl sm:text-6xl font-black text-gray-900 mb-4">
            Látogass el hozzánk!
          </h1>
          <p className="text-xl text-gray-600">
            Megtalálsz minket Szigethalmon – Várunk szeretettel!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Open Status Indicator */}
            <OpenStatusIndicator />

            {/* Address */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#00BCD4] to-[#00ACC1] rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Címünk</h3>
                  <p className="text-gray-700 text-lg">
                    {restaurantInfo.address.zip} {restaurantInfo.address.city}
                  </p>
                  <p className="text-gray-700 text-lg">
                    {restaurantInfo.address.street}
                  </p>
                  <a
                    href="https://www.google.com/maps/dir//2315+Szigethalom,+Sz%C3%A9chenyi+u.+44"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 mt-4 px-6 py-3 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <Navigation className="w-5 h-5" />
                    <span>Útvonal megtekintése</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#FFA726] to-[#FF9800] rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Telefonszám</h3>
                  <p className="text-gray-700 text-lg mb-4">
                    {restaurantInfo.phone}
                  </p>
                  <a
                    href={`tel:${restaurantInfo.phone}`}
                    className="inline-block px-6 py-3 bg-gradient-to-r from-[#FFA726] to-[#FF9800] text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Hívás most
                  </a>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#00BCD4] to-[#00ACC1] rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Nyitvatartás</h3>

                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#00BCD4]/10 to-[#FFA726]/10 border-2 border-[#00BCD4]">
                    <div className="space-y-2">
                      {openingHours.map((day) => (
                        <div
                          key={day.day}
                          className="flex justify-between items-center py-1.5"
                        >
                          <span className="font-semibold text-gray-900">{day.day}:</span>
                          <span
                            className={`font-medium ${
                              day.hours === 'Zárva' ? 'text-red-500' : 'text-[#00BCD4]'
                            }`}
                          >
                            {day.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="bg-gradient-to-r from-[#00BCD4] to-[#FFA726] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Szolgáltatásaink</h3>
              <ul className="space-y-3">
                {restaurantInfo.services.map((service, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <span className="text-2xl">✓</span>
                    <span className="text-lg font-medium">{service}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-white/90 text-sm italic">
                * Asztalfoglalást nem fogadunk el. Várjuk szeretettel helyben vagy elvitelre!
              </p>
            </div>
          </div>

          {/* Map */}
          <div className="rounded-2xl overflow-hidden shadow-2xl h-[800px] lg:h-auto">
            <iframe
              title="Boldog Büfé location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2702.8!2d18.9973!3d47.3204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741c5a9a3b8e9c3%3A0x5a4e5d3b8c9d7e6f!2s2315%20Szigethalom%2C%20Sz%C3%A9chenyi%20u.%2044%2C%20Hungary!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>

        {/* Delivery Fees by Town */}
        <section id="szallitasi-dijak" className="scroll-mt-24 mb-12">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#00BCD4] to-[#00ACC1] rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
                  Házhozszállítás
                </h2>
                <p className="text-gray-600">
                  Házhozszállítást kizárólag Szigethalmon belül vállalunk.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 max-w-md">
              {deliveryZones.map((zone) => (
                <div
                  key={zone.id}
                  className="flex items-center justify-between p-5 bg-gradient-to-r from-[#00BCD4]/10 to-white border-2 border-[#00BCD4] rounded-xl shadow-md"
                >
                  <span className="font-bold text-gray-900 text-xl">{zone.name}</span>
                  <span className="font-black text-[#00BCD4] text-xl">
                    {zone.fee.toLocaleString('hu-HU')} Ft
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-6 text-sm text-gray-600 italic">
              * Egyéb településekre nem tudunk szállítást vállalni — kérjük válassza az elvitelt
              vagy a helyben fogyasztást, vagy hívjon minket:{' '}
              <a
                href={`tel:${restaurantInfo.phone}`}
                className="text-[#00BCD4] font-semibold hover:underline not-italic"
              >
                {restaurantInfo.phone}
              </a>
              .
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-br from-gray-50 to-white rounded-2xl p-12 border border-gray-200">
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
            Éhes vagy? Rendelj most!
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Nézd meg étlapunkat és rendelj online vagy telefonon!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/menu"
              className="inline-block px-8 py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Étlap megtekintése
            </a>
            <a
              href="tel:+36309517159"
              className="inline-block px-8 py-4 bg-gradient-to-r from-[#FFA726] to-[#FF9800] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Telefonos Rendelés
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
